import { createImmutableEvent, deepFreeze } from './workflow'
import type {
  AiGarmentSuggestion,
  AppNotification,
  DeviceSettings,
  Evidence,
  FactoryInformationOverride,
  FactoryInspection,
  Garment,
  ImmutableDomainEvent,
  LabelVersion,
  Member,
  MobileSnapshot,
  Order,
  Rack,
  WorkflowEventType,
  WorkflowState,
} from './types'

const members: readonly Member[] = [
  {
    id: 'MEM-00821', cardNo: 'M00821', name: '王女士', phone: '13800008276', phoneMasked: '138****8276',
    membership: '金卡会员', balance: 860, points: 1280, preference: '无香护理', address: '江山风华 2 号楼 1203',
    createdAt: '2025-11-06T10:20:00+08:00',
  },
  {
    id: 'MEM-00914', cardNo: 'M00914', name: '林女士', phone: '13800138000', phoneMasked: '138****8000',
    membership: '银卡会员', balance: 356, points: 680, preference: '低温烘干', address: '万象里 8 号楼 602',
    createdAt: '2026-01-18T09:30:00+08:00',
  },
  {
    id: 'MEM-01003', cardNo: 'M01003', name: '周先生', phone: '13900139000', phoneMasked: '139****9000',
    membership: '普通会员', balance: 128, points: 320, address: '金茂府 5 号楼 1101',
    createdAt: '2026-03-12T14:20:00+08:00',
  },
]

const activeLabel = (shortCode: string, suffix: string): LabelVersion => ({
  id: `LBL-${suffix}-V1`, version: 1, status: 'ACTIVE', barcode: `BC-${shortCode}`, epc: `E280-${suffix.padStart(8, '0')}`,
  activatedAt: '2026-07-23T10:18:00+08:00',
})

const draftLabel = (shortCode: string, suffix: string): LabelVersion => ({
  id: `LBL-${suffix}-V1`, version: 1, status: 'DRAFT', barcode: `BC-${shortCode}`, epc: `E280-${suffix.padStart(8, '0')}`,
})

const makeGarment = (input: {
  id: string
  shortCode: string
  orderId: string
  category: string
  color: string
  material: string
  service: string
  price: number
  state: WorkflowState
  suffix: string
  conditionNote?: string
  riskFlags?: readonly string[]
  factoryInspections?: readonly FactoryInspection[]
  factoryOverrides?: readonly FactoryInformationOverride[]
  returnedToStoreAt?: string
  rackId?: string
  pickupVerifiedAt?: string
  aiSuggestions?: readonly AiGarmentSuggestion[]
  evidenceIds?: readonly string[]
}): Garment => ({
  id: input.id,
  shortCode: input.shortCode,
  orderId: input.orderId,
  category: input.category,
  color: input.color,
  material: input.material,
  service: input.service,
  price: input.price,
  conditionNote: input.conditionNote,
  riskFlags: input.riskFlags ?? [],
  workflowState: input.state,
  labels: input.state === 'DRAFT' || input.state === 'PRICED' || input.state === 'PAID'
    ? [draftLabel(input.shortCode, input.suffix)]
    : [activeLabel(input.shortCode, input.suffix)],
  aiSuggestions: input.aiSuggestions ?? [],
  factoryInspections: input.factoryInspections ?? [],
  factoryOverrides: input.factoryOverrides ?? [],
  rackBindings: input.rackId ? [{ rackId: input.rackId, boundAt: '2026-07-24T15:20:00+08:00', boundBy: 'staff_shelf_01', active: true }] : [],
  evidenceIds: input.evidenceIds ?? [],
  sentToFactoryAt: input.state === 'DRAFT' || input.state === 'PRICED' || input.state === 'PAID' || input.state === 'LABEL_ACTIVE' || input.state === 'INTAKE_COMPLETE'
    ? undefined : '2026-07-23T18:30:00+08:00',
  washCompletedAt: ['WAITING_QC', 'QC_PASSED', 'EXCEPTION', 'WAITING_SHELF', 'SHELVED', 'WAITING_PICKUP', 'PICKUP_VERIFYING', 'COMPLETED'].includes(input.state)
    ? '2026-07-24T13:20:00+08:00' : undefined,
  returnedToStoreAt: input.returnedToStoreAt,
  pickupVerifiedAt: input.pickupVerifiedAt,
})

const inspectionIssue: FactoryInspection = {
  id: 'FINSP-001-A', status: 'ISSUE', issueType: '色差', note: '右袖口仍有轻微色差，已同步门店',
  inspectorId: 'factory_qc_01', inspectedAt: '2026-07-24T13:28:00+08:00', syncedToStoreAt: '2026-07-24T13:28:03+08:00',
  evidenceIds: ['EVD-RETURN-001'],
}

const inspectionPassed: FactoryInspection = {
  id: 'FINSP-002-B', status: 'PASSED', inspectorId: 'factory_qc_02',
  inspectedAt: '2026-07-24T13:31:00+08:00', syncedToStoreAt: '2026-07-24T13:31:02+08:00', evidenceIds: [],
}

const factoryOverride: FactoryInformationOverride = {
  id: 'FOVR-002-A', operatorId: 'factory_qc_02', occurredAt: '2026-07-24T13:35:00+08:00', reason: '工厂实物复核',
  before: { category: '羊毛大衣', color: '卡其色', material: '羊毛80%', service: '干洗', conditionNote: '右袖口油渍' },
  after: { category: '羊毛大衣', color: '浅卡其', material: '羊毛80%', service: '精细干洗', conditionNote: '右袖口轻微色差' },
}

const orders: readonly Order[] = [
  {
    id: 'ORD-M-20260725-001', memberId: 'MEM-00821', source: 'STORE', createdBy: 'staff_front_01',
    createdAt: '2026-07-23T10:00:00+08:00', promisedAt: '2026-07-26T18:00:00+08:00', note: '工厂异常待处理',
    garments: [
      {
        ...makeGarment({
          id: 'GAR-M-001-A', shortCode: 'A7K-3F2', orderId: 'ORD-M-20260725-001', category: '羊毛大衣', color: '卡其色',
          material: '羊毛80%', service: '干洗', price: 68, state: 'EXCEPTION', suffix: '1001', conditionNote: '右袖口油渍',
          riskFlags: ['HIGH_VALUE', 'OIL_STAIN'], factoryInspections: [inspectionIssue], evidenceIds: ['EVD-RETURN-001'],
        }),
        exception: {
          reason: '工厂二检发现右袖口色差', resumeState: 'WAITING_QC',
          registeredAt: '2026-07-24T13:28:05+08:00', registeredBy: 'factory_qc_01',
        },
      },
      makeGarment({
        id: 'GAR-M-001-B', shortCode: 'B2M-9Q1', orderId: 'ORD-M-20260725-001', category: '羽绒服', color: '白色',
        material: '羽绒', service: '精洗', price: 58, state: 'WAITING_QC', suffix: '1002',
        riskFlags: ['MINOR_DAMAGE'], factoryInspections: [inspectionPassed],
      }),
    ],
    amounts: { original: 126, discount: 12, confirmed: 114, recalculated: 114, currency: 'CNY' },
    pricingReadiness: { pricingBlockersCount: 0, allImagesUploaded: true, allRequiredRiskAcknowledgementsConfirmed: true },
    payment: { id: 'PAY-M-001', status: 'SUCCEEDED', method: 'MEMBER_BALANCE', amount: 114, paidAt: '2026-07-23T10:17:00+08:00' },
    initialLabelPrintJob: { id: 'PRN-M-001', status: 'SUCCEEDED', successCount: 2, failedCount: 0 },
    dispatchBatchId: 'B-0723-03', customerIdentityConfirmed: false, pickupRiskAndQuantityConfirmed: false,
  },
  {
    id: 'ORD-M-20260725-002', memberId: 'MEM-00914', source: 'STORE', createdBy: 'staff_front_02',
    createdAt: '2026-07-22T09:12:00+08:00', promisedAt: '2026-07-25T19:00:00+08:00', note: '回件已到店，待上架',
    garments: [
      makeGarment({
        id: 'GAR-M-002-A', shortCode: 'R3T-6C8', orderId: 'ORD-M-20260725-002', category: '羊毛大衣', color: '浅卡其',
        material: '羊毛80%', service: '精细干洗', price: 68, state: 'WAITING_SHELF', suffix: '2001',
        conditionNote: '右袖口轻微色差', factoryInspections: [inspectionIssue], factoryOverrides: [factoryOverride],
        returnedToStoreAt: '2026-07-25T09:08:00+08:00', evidenceIds: ['EVD-RETURN-002'],
      }),
      makeGarment({
        id: 'GAR-M-002-B', shortCode: 'D4W-2H7', orderId: 'ORD-M-20260725-002', category: '西装上衣', color: '藏青色',
        material: '羊毛混纺', service: '干洗整形', price: 52, state: 'WAITING_SHELF', suffix: '2002',
        factoryInspections: [inspectionPassed], returnedToStoreAt: '2026-07-25T09:08:30+08:00',
      }),
    ],
    amounts: { original: 120, discount: 12, confirmed: 108, recalculated: 108, currency: 'CNY' },
    pricingReadiness: { pricingBlockersCount: 0, allImagesUploaded: true, allRequiredRiskAcknowledgementsConfirmed: true },
    payment: { id: 'PAY-M-002', status: 'SUCCEEDED', method: 'WECHAT', amount: 108, paidAt: '2026-07-22T09:25:00+08:00' },
    initialLabelPrintJob: { id: 'PRN-M-002', status: 'SUCCEEDED', successCount: 2, failedCount: 0 },
    dispatchBatchId: 'B-0722-01', customerIdentityConfirmed: false, pickupRiskAndQuantityConfirmed: false,
  },
  {
    id: 'ORD-M-20260725-003', memberId: 'MEM-01003', source: 'WECHAT', createdBy: 'wechat_miniapp',
    createdAt: '2026-07-21T16:20:00+08:00', promisedAt: '2026-07-25T16:00:00+08:00', note: '顾客将到店自取',
    garments: [
      makeGarment({
        id: 'GAR-M-003-A', shortCode: 'P8L-5N2', orderId: 'ORD-M-20260725-003', category: '风衣', color: '米色',
        material: '棉锦混纺', service: '精洗', price: 48, state: 'WAITING_PICKUP', suffix: '3001', rackId: 'A-12-01',
        returnedToStoreAt: '2026-07-24T15:01:00+08:00', factoryInspections: [inspectionPassed],
      }),
      makeGarment({
        id: 'GAR-M-003-B', shortCode: 'Q1S-4M9', orderId: 'ORD-M-20260725-003', category: '真丝衬衫', color: '米白色',
        material: '真丝', service: '手洗', price: 68, state: 'WAITING_PICKUP', suffix: '3002', rackId: 'A-12-02',
        returnedToStoreAt: '2026-07-24T15:02:00+08:00', factoryInspections: [inspectionPassed],
      }),
      makeGarment({
        id: 'GAR-M-003-C', shortCode: 'V6J-7X3', orderId: 'ORD-M-20260725-003', category: '羊绒围巾', color: '灰色',
        material: '羊绒', service: '手工护理', price: 46, state: 'WAITING_PICKUP', suffix: '3003', rackId: 'A-12-03',
        returnedToStoreAt: '2026-07-24T15:03:00+08:00', factoryInspections: [inspectionPassed],
      }),
    ],
    amounts: { original: 162, discount: 0, confirmed: 162, recalculated: 162, currency: 'CNY' },
    pricingReadiness: { pricingBlockersCount: 0, allImagesUploaded: true, allRequiredRiskAcknowledgementsConfirmed: true },
    payment: { id: 'PAY-M-003', status: 'SUCCEEDED', method: 'WECHAT', amount: 162, paidAt: '2026-07-21T16:25:00+08:00' },
    initialLabelPrintJob: { id: 'PRN-M-003', status: 'SUCCEEDED', successCount: 3, failedCount: 0 },
    dispatchBatchId: 'B-0721-04', customerIdentityConfirmed: false, pickupRiskAndQuantityConfirmed: false,
  },
  {
    id: 'ORD-M-20260725-004', memberId: 'MEM-00821', source: 'MOBILE', createdBy: 'staff_mobile_01',
    createdAt: '2026-07-25T09:45:00+08:00', promisedAt: '2026-07-28T18:00:00+08:00', note: '移动开单草稿',
    garments: [
      makeGarment({
        id: 'GAR-M-004-A', shortCode: 'M4B-8K6', orderId: 'ORD-M-20260725-004', category: '待人工确认', color: '深色',
        material: '待识别', service: '待确认', price: 0, state: 'DRAFT', suffix: '4001',
        aiSuggestions: [{
          id: 'AI-M-004-A', category: '羊绒开衫', color: '深灰色', material: '羊绒（疑似）', service: '手工护理',
          conditionNote: '左前襟疑似油渍', riskFlags: ['HIGH_VALUE', 'OIL_STAIN'], confidence: 0.87,
          findings: ['洗标识别置信度偏低', '左前襟疑似油渍'], requiresHumanReview: true, createdAt: '2026-07-25T09:46:00+08:00',
        }],
      }),
    ],
    amounts: { original: 0, discount: 0, confirmed: 0, recalculated: 0, currency: 'CNY' },
    pricingReadiness: { pricingBlockersCount: 1, allImagesUploaded: false, allRequiredRiskAcknowledgementsConfirmed: false },
    payment: { id: 'PAY-M-004', status: 'PENDING', amount: 0 },
    initialLabelPrintJob: { id: 'PRN-M-004', status: 'QUEUED', successCount: 0, failedCount: 0 },
    customerIdentityConfirmed: false, pickupRiskAndQuantityConfirmed: false,
  },
]

const evidence: readonly Evidence[] = [
  {
    id: 'EVD-RETURN-001', orderId: 'ORD-M-20260725-001', garmentId: 'GAR-M-001-A', kind: 'FACTORY_QC_PHOTO',
    uri: 'demo://evidence/factory-color-difference.jpg', note: '工厂二检色差留证', capturedBy: 'factory_qc_01',
    capturedAt: '2026-07-24T13:27:50+08:00', metadata: { fixture: true },
  },
  {
    id: 'EVD-RETURN-002', orderId: 'ORD-M-20260725-002', garmentId: 'GAR-M-002-A', kind: 'RETURN_PHOTO',
    uri: 'demo://evidence/returned-garment.jpg', note: '回件到店留证', capturedBy: 'staff_return_01',
    capturedAt: '2026-07-25T09:08:05+08:00', metadata: { fixture: true },
  },
]

const transitionPath: readonly { from: WorkflowState; to: WorkflowState; eventType: WorkflowEventType }[] = [
  { from: 'DRAFT', to: 'PRICED', eventType: 'PRICING_CONFIRMED' },
  { from: 'PRICED', to: 'PAID', eventType: 'SIMULATED_PAYMENT_SUCCEEDED' },
  { from: 'PAID', to: 'LABEL_ACTIVE', eventType: 'LABELS_ACTIVATED' },
  { from: 'LABEL_ACTIVE', to: 'INTAKE_COMPLETE', eventType: 'INTAKE_COMPLETED' },
  { from: 'INTAKE_COMPLETE', to: 'SENT_TO_PLANT', eventType: 'SENT_TO_PLANT' },
  { from: 'SENT_TO_PLANT', to: 'PLANT_RECEIVED', eventType: 'PLANT_RECEIVED' },
  { from: 'PLANT_RECEIVED', to: 'WASHING', eventType: 'WASHING_STARTED' },
  { from: 'WASHING', to: 'WAITING_QC', eventType: 'WASHING_COMPLETED' },
  { from: 'WAITING_QC', to: 'QC_PASSED', eventType: 'QC_PASSED' },
  { from: 'QC_PASSED', to: 'WAITING_SHELF', eventType: 'READY_FOR_SHELVING' },
  { from: 'WAITING_SHELF', to: 'SHELVED', eventType: 'RACK_BOUND' },
  { from: 'SHELVED', to: 'WAITING_PICKUP', eventType: 'ORDER_READY_FOR_PICKUP' },
]

const seedHistoryFor = (order: Order, garment: Garment, orderIndex: number): ImmutableDomainEvent[] => {
  const events: ImmutableDomainEvent[] = []
  const target = garment.workflowState === 'EXCEPTION' ? 'WAITING_QC' : garment.workflowState
  if (target === 'DRAFT') return events
  for (const [index, transition] of transitionPath.entries()) {
    events.push(createImmutableEvent({
      id: `EVT-SEED-${orderIndex}-${garment.id}-${index + 1}`, type: transition.eventType, orderId: order.id, garmentId: garment.id,
      operatorId: index < 4 ? 'staff_front_01' : index < 8 ? 'factory_fulfill_01' : 'staff_shelf_01',
      occurredAt: `2026-07-${String(22 + Math.min(orderIndex, 2)).padStart(2, '0')}T${String(9 + index).padStart(2, '0')}:00:00+08:00`,
      idempotencyKey: `seed-${order.id}-${garment.id}-${index + 1}`, fromState: transition.from, toState: transition.to,
      metadata: { seed: true },
    }))
    if (transition.to === target) break
  }
  if (garment.workflowState === 'EXCEPTION' && garment.exception) {
    events.push(createImmutableEvent({
      id: `EVT-SEED-${orderIndex}-${garment.id}-EX`, type: 'EXCEPTION_REGISTERED', orderId: order.id, garmentId: garment.id,
      operatorId: garment.exception.registeredBy, occurredAt: garment.exception.registeredAt,
      idempotencyKey: `seed-${order.id}-${garment.id}-exception`, fromState: garment.exception.resumeState, toState: 'EXCEPTION',
      metadata: { reason: garment.exception.reason, resumeState: garment.exception.resumeState },
    }))
  }
  return events
}

const events: readonly ImmutableDomainEvent[] = orders.flatMap((order, orderIndex) => [
  createImmutableEvent({
    id: `EVT-SEED-${orderIndex}-CREATE`, type: 'ORDER_DRAFT_CREATED', orderId: order.id, operatorId: order.createdBy,
    occurredAt: order.createdAt, metadata: { source: order.source },
  }),
  ...order.garments.flatMap((garment) => seedHistoryFor(order, garment, orderIndex)),
])

const notifications: readonly AppNotification[] = [
  {
    id: 'NTF-001', kind: 'EXCEPTION', title: '工厂二检异常', body: 'A7K-3F2 发现右袖口色差，请复核处理。',
    createdAt: '2026-07-24T13:28:05+08:00', orderId: 'ORD-M-20260725-001', garmentId: 'GAR-M-001-A',
  },
  {
    id: 'NTF-002', kind: 'RETURN', title: '回件到店', body: '订单 ORD-M-20260725-002 已完成 2/2 回件对账。',
    createdAt: '2026-07-25T09:09:00+08:00', orderId: 'ORD-M-20260725-002',
  },
  {
    id: 'NTF-003', kind: 'PICKUP', title: '顾客待取', body: '订单 ORD-M-20260725-003 共 3 件，等待 N/N 自取核验。',
    createdAt: '2026-07-25T09:12:00+08:00', orderId: 'ORD-M-20260725-003',
  },
]

const deviceSettings: DeviceSettings = {
  preferredScanMode: 'RFID',
  scanner: { id: 'scanner_mobile_01', status: 'CONNECTED' },
  rfidReader: { id: 'rfid_mobile_01', status: 'CONNECTED' },
  camera: { id: 'camera_mobile_01', status: 'ONLINE' },
  printer: { id: 'PT-210', status: 'ONLINE', paperPercent: 68, labelWidthMm: 50, labelHeightMm: 30 },
  network: { status: 'ONLINE' }, soundEnabled: true, vibrationEnabled: true, autoPrintLabels: true,
}

const racks: readonly Rack[] = [
  { id: 'A-12-01', status: 'OCCUPIED', activeGarmentId: 'GAR-M-003-A' },
  { id: 'A-12-02', status: 'OCCUPIED', activeGarmentId: 'GAR-M-003-B' },
  { id: 'A-12-03', status: 'OCCUPIED', activeGarmentId: 'GAR-M-003-C' },
  { id: 'A-12-04', status: 'AVAILABLE' },
  { id: 'A-12-05', status: 'LOCKED' },
  { id: 'B-03-01', status: 'AVAILABLE' },
  { id: 'B-03-02', status: 'AVAILABLE' },
]

export const createDemoSnapshot = (): MobileSnapshot => deepFreeze({
  schemaVersion: '1.0.0',
  members,
  orders,
  evidence,
  events,
  notifications,
  deviceSettings,
  racks,
  reconciliations: [],
  processedCommandKeys: [],
}) as MobileSnapshot
